import { z } from 'zod';
import { AccessState, Id, Role } from '../primitives.js';

/**
 * Контракты модуля Identity & Access: регистрация, вход, обновление сессии.
 *
 * Документация модуля: docs/03-modules/01-identity-access.md
 */

/**
 * Минимальная длина пароля живёт здесь, а не только на сервере: форма обязана
 * сказать о слишком коротком пароле до отправки. Остальные правила (списки
 * частых паролей, совпадение с адресом) проверяются на сервере — они требуют
 * данных, которых у формы нет.
 */
export const Password = z.string().min(10).max(200);

export const TenantKind = z.enum(['clinic', 'lab']);
export type TenantKind = z.infer<typeof TenantKind>;

export const RegisterRequest = z.object({
  tenant: z.object({
    name: z.string().min(2).max(200),
    kind: TenantKind,
  }),
  owner: z.object({
    email: z.email(),
    password: Password,
    lastName: z.string().min(1).max(100),
    firstName: z.string().min(1).max(100),
    middleName: z.string().max(100).optional(),
    phone: z.string().regex(/^\+?\d{10,15}$/).optional(),
  }),
  /**
   * Код реферальной ссылки менеджера Odonix (`odonix.ru/r/<код>`), который
   * сайт запомнил при переходе. Формат здесь не проверяется: код из чужого
   * браузера, и кривое значение не повод отказать в регистрации — ядро
   * отбросит его само.
   */
  referralCode: z.string().max(64).optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequest>;

export const LoginRequest = z.object({
  email: z.email(),
  /** Без ограничений по длине: проверять политику на входе поздно и незачем. */
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

/**
 * Второй шаг входа при включённой MFA: `challengeToken` вместо пароля,
 * который на этот момент уже проверен, и код из приложения-аутентификатора
 * либо резервный код на восемь символов — сервер не различает их наружу,
 * пробует оба.
 */
export const VerifyMfaLoginRequest = z.object({
  challengeToken: z.string().min(1),
  code: z.string().min(1).max(20),
});
export type VerifyMfaLoginRequest = z.infer<typeof VerifyMfaLoginRequest>;

export const MfaEnrollResponse = z.object({
  secret: z.string(),
  otpauthUri: z.string(),
});
export type MfaEnrollResponse = z.infer<typeof MfaEnrollResponse>;

export const ConfirmMfaEnrollmentRequest = z.object({
  code: z.string().min(1).max(20),
});
export type ConfirmMfaEnrollmentRequest = z.infer<typeof ConfirmMfaEnrollmentRequest>;

export const MfaRecoveryCodesResponse = z.object({
  recoveryCodes: z.array(z.string()),
});
export type MfaRecoveryCodesResponse = z.infer<typeof MfaRecoveryCodesResponse>;

export const DisableMfaRequest = z.object({
  password: z.string().min(1).max(200),
});
export type DisableMfaRequest = z.infer<typeof DisableMfaRequest>;

export const MfaStatusResponse = z.object({
  enabled: z.boolean(),
  liveRecoveryCodes: z.number().nullable(),
});
export type MfaStatusResponse = z.infer<typeof MfaStatusResponse>;

/** Для нативных оболочек: в браузере refresh живёт в HttpOnly-cookie. */
export const RefreshRequest = z.object({
  refreshToken: z.string().min(1),
});

/**
 * Состояние членства. Приглашённый ещё ни разу не входил, отстранённый временно
 * не работает, деактивированный уволен.
 *
 * Уволенный из справочника не исчезает: под договором прошлого года стоит его
 * подпись, и расшифровать её должно быть чем. Вернувшийся на работу получает то
 * же членство обратно в `active` — пара «клиника — человек» уникальна, и второе
 * место работы в одной клинике означало бы две роли у одного человека.
 */
export const MembershipStatus = z.enum(['invited', 'active', 'suspended', 'deactivated']);
export type MembershipStatus = z.infer<typeof MembershipStatus>;

/**
 * Идентификатор права — строка, а не перечисление: перечень прав живёт в
 * `@odonix/core`, куда контракты зависеть не могут, и сервер сверяет
 * значение с ним сам. Версия клиента младше сервера не должна падать на
 * праве, которого ещё не знает.
 */
export const PermissionId = z.string().min(3).max(64);

/**
 * Поправка владельца к правам роли: что выдано сверх матрицы, что отозвано
 * из неё. Приходит вместе с членством, чтобы клиент считал права так же, как
 * сервер, и без сети тоже — из того снимка, что был при последнем входе.
 */
export const PermissionOverrides = z.object({
  granted: z.array(PermissionId).max(200),
  revoked: z.array(PermissionId).max(200),
});
export type PermissionOverrides = z.infer<typeof PermissionOverrides>;

export const MembershipView = z.object({
  tenantId: Id,
  tenantName: z.string(),
  tenantKind: TenantKind,
  role: Role,
  accessState: AccessState,
  status: MembershipStatus,
  /**
   * Пусто (`null`) у ролей со стандартными правами; отсутствует вовсе у
   * ответа старого сервера и у снимков сессии, сохранённых до этого поля.
   */
  permissionOverrides: PermissionOverrides.nullable().optional(),
});
export type MembershipView = z.infer<typeof MembershipView>;

/** Права одной роли глазами владельца: стандарт, поправка и итог. */
export const RolePermissionsView = z.object({
  role: Role,
  base: z.array(PermissionId),
  granted: z.array(PermissionId),
  revoked: z.array(PermissionId),
  effective: z.array(PermissionId),
});
export type RolePermissionsView = z.infer<typeof RolePermissionsView>;

export const RolePermissionsResponse = z.object({
  roles: z.array(RolePermissionsView),
  /** Что владелец вправе выдать: его собственные права минус неделегируемые. */
  grantable: z.array(PermissionId),
});
export type RolePermissionsResponse = z.infer<typeof RolePermissionsResponse>;

export const UpdateRolePermissionsRequest = z.object({
  granted: z.array(PermissionId).max(200),
  revoked: z.array(PermissionId).max(200),
});
export type UpdateRolePermissionsRequest = z.infer<typeof UpdateRolePermissionsRequest>;

/**
 * Сброс доступа всем сотрудникам разом: новые пароли и закрытые сессии у
 * всех, кроме владельцев. Подтверждается паролем самого владельца: кнопка
 * стоит на каждом экране, и случайное нажатие не должно выгонять клинику из
 * системы.
 */
export const ResetStaffAccessRequest = z.object({
  password: z.string().min(1).max(200),
});
export type ResetStaffAccessRequest = z.infer<typeof ResetStaffAccessRequest>;

export const ResetStaffAccessEntry = z.object({
  membershipId: Id,
  lastName: z.string(),
  firstName: z.string(),
  role: Role,
  email: z.string(),
  /** Новый пароль, 8 знаков. Показывается один раз и больше нигде не хранится. */
  password: z.string(),
});
export type ResetStaffAccessEntry = z.infer<typeof ResetStaffAccessEntry>;

export const ResetStaffAccessResponse = z.object({
  count: z.number().int().nonnegative(),
  /** Письмо с паролями ушло владельцу; `false` — почта не настроена или не ответила, пароли только в этом ответе. */
  emailed: z.boolean(),
  ownerEmail: z.string(),
  entries: z.array(ResetStaffAccessEntry),
});
export type ResetStaffAccessResponse = z.infer<typeof ResetStaffAccessResponse>;

/**
 * Ответ на вход. `refreshToken` присутствует только в ответе нативным
 * клиентам: браузеру он уходит в cookie, недоступную из JavaScript, и в теле
 * его быть не должно.
 */
export const AuthResponse = z.object({
  accessToken: z.string(),
  refreshToken: z.string().optional(),
  userId: Id,
  memberships: z.array(MembershipView),
});
export type AuthResponse = z.infer<typeof AuthResponse>;

/**
 * Пароль верный, но у аккаунта включена MFA — вместо токенов приходит
 * `challengeToken` для второго шага. `mfaRequired: true` как дискриминатор,
 * а не отсутствие `accessToken`: отличать «нет поля» от «поля нет, потому что
 * что-то не так» на клиенте плохо получается.
 */
export const MfaRequiredResponse = z.object({
  mfaRequired: z.literal(true),
  challengeToken: z.string(),
});
export type MfaRequiredResponse = z.infer<typeof MfaRequiredResponse>;

export const LoginResponse = z.union([AuthResponse, MfaRequiredResponse]);
export type LoginResponse = z.infer<typeof LoginResponse>;

export const MeResponse = z.object({
  userId: Id,
  email: z.email(),
  lastName: z.string(),
  firstName: z.string(),
  middleName: z.string().nullable(),
  /** Пусто у ответа старого сервера: клиент без телефона в профиле не ломается. */
  phone: z.string().nullable().default(null),
  memberships: z.array(MembershipView),
  /** Арендатор текущего запроса: пусто, когда клиент его ещё не выбрал. */
  currentTenantId: Id.nullable(),
});
export type MeResponse = z.infer<typeof MeResponse>;

/**
 * Правка своей учётной записи: ФИО и телефон. Только своей — право здесь не
 * нужно, как и у MFA: человек распоряжается собственным именем в любой роли.
 * E-mail не меняется этим запросом: он же логин, и его смена — отдельный
 * сценарий с подтверждением нового адреса.
 */
export const UpdateMeRequest = z
  .object({
    lastName: z.string().trim().min(1).max(100).optional(),
    firstName: z.string().trim().min(1).max(100).optional(),
    middleName: z
      .string()
      .trim()
      .max(100)
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .optional(),
    phone: z
      .preprocess(
        (value) => (typeof value === 'string' ? (value.trim() === '' ? null : value.trim()) : value),
        z.string().regex(/^\+?\d{10,15}$/, 'Телефон — от 10 до 15 цифр, можно с «+»').nullable(),
      )
      .optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Изменение учётной записи пустое',
  });
export type UpdateMeRequest = z.infer<typeof UpdateMeRequest>;

/** Заголовок выбора арендатора: значение — один из своих tenantId. */
export const TENANT_HEADER = 'x-odonix-tenant';

/**
 * Заголовок, которым нативная оболочка объявляет себя. Браузер его не ставит.
 *
 * От него зависит, куда уходит refresh-токен: браузеру — в HttpOnly-cookie,
 * оболочке — в тело ответа, чтобы она положила его в хранилище ОС. Страница
 * оболочки живёт на `tauri.localhost`, и cookie с `app.odonix.ru` к ней не
 * вернётся — cross-site запрос её не несёт, и без этого заголовка сессия в
 * оболочке жила бы ровно до первого истечения access-токена.
 */
export const CLIENT_HEADER = 'x-odonix-client';
export const NativeClient = z.enum(['desktop', 'mobile']);
export type NativeClient = z.infer<typeof NativeClient>;

/**
 * Устройство, с которого работают, — с точки зрения человека это и есть
 * сессия. `current` не хранится в базе: сервер сравнивает id из списка с id
 * сессии текущего запроса на каждый вызов, иначе список молчал бы правду
 * после отзыва «этого» устройства в отдельной вкладке.
 */
export const SessionView = z.object({
  id: Id,
  createdAt: z.iso.datetime(),
  /** Последняя ротация refresh-токена — фактическая активность устройства. */
  lastActiveAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  current: z.boolean(),
});
export type SessionView = z.infer<typeof SessionView>;

export const SessionListResponse = z.object({
  sessions: z.array(SessionView),
});
export type SessionListResponse = z.infer<typeof SessionListResponse>;

/**
 * Строка журнала доступа. `action` и `entity` — открытые строки, а не enum:
 * список сущностей растёт с каждым модулем, и версия клиента младше сервера
 * не должна упасть на записи о том, чего она пока не знает, — она обязана
 * показать её как есть.
 */
export const AuditEventView = z.object({
  id: Id,
  actorId: Id.nullable(),
  action: z.string(),
  entity: z.string(),
  entityId: Id.nullable(),
  patientId: Id.nullable(),
  occurredAt: z.iso.datetime(),
  meta: z.record(z.string(), z.unknown()),
});
export type AuditEventView = z.infer<typeof AuditEventView>;

export const AuditEventListResponse = z.object({
  events: z.array(AuditEventView),
  /** `null` — страница последняя, дальше нечего запрашивать. */
  nextCursor: z.string().nullable(),
});
export type AuditEventListResponse = z.infer<typeof AuditEventListResponse>;

/**
 * Область доступа поддержки. Закрытый список, а не свободный текст: форма
 * предлагает конкретные модули, чтобы владелец выбирал минимально нужное, а
 * не переписывал одну и ту же формулировку от гранта к гранту.
 */
export const SupportGrantScope = z.enum([
  'patients',
  'clinical',
  'scheduling',
  'finance',
  'staff',
  'settings',
]);
export type SupportGrantScope = z.infer<typeof SupportGrantScope>;

export const IssueSupportGrantRequest = z.object({
  supportEmail: z.email(),
  reason: z.string().min(1).max(500),
  scope: z.array(SupportGrantScope).min(1),
  /** Часы, не миллисекунды: форма спрашивает «на сколько», а не «до какого
   *  момента» — точное время выдачи всё равно решает сервер. */
  hours: z.number().int().min(1).max(72),
});
export type IssueSupportGrantRequest = z.infer<typeof IssueSupportGrantRequest>;

export const SupportGrantView = z.object({
  id: Id,
  grantedById: Id,
  supportUserId: Id,
  /** Email тех, кого хранилище знает только по `id` — тут уже развёрнуто. */
  grantedByEmail: z.string(),
  supportEmail: z.string(),
  reason: z.string(),
  scope: z.array(z.string()),
  grantedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
  /** Сервер решает это по своим часам — клиенту сравнивать даты незачем и
   *  небезопасно: его часы могут расходиться с сервером сильнее, чем думает. */
  active: z.boolean(),
});
export type SupportGrantView = z.infer<typeof SupportGrantView>;

export const SupportGrantListResponse = z.object({
  grants: z.array(SupportGrantView),
});
export type SupportGrantListResponse = z.infer<typeof SupportGrantListResponse>;
